import React from "react";
import { Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import { ArrowLeftIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../common/general/CustomText/CustomText";
import { colors } from "../../../styles/design-system";

interface GapActionDrawerLayoutProps {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onBack?: () => void;
  onClose?: () => void;
  width?: number;
  height?: number;
  containerStyle?: ViewStyle;
  borderRadius?: number;
  showCloseButton?: boolean;
  backLabel?: string;
  accessibilityLabel?: string;
}

function GapActionDrawerLayout({
  title,
  children,
  footer,
  onBack,
  onClose,
  width,
  height,
  containerStyle,
  borderRadius = 8,
  showCloseButton = true,
  backLabel = "Gap analysis",
  accessibilityLabel = "Gap action drawer",
}: GapActionDrawerLayoutProps) {
  return (
    <View
      style={[
        styles.drawerShell,
        { borderRadius },
        width ? { width } : undefined,
        height ? { height } : undefined,
        containerStyle,
      ]}
      role="dialog"
      accessibilityLabel={accessibilityLabel}>
      <View style={styles.header}>
        <View style={styles.headerTopRow}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back to gap analysis"
            onPress={onBack}
            style={styles.backButton}>
            <ArrowLeftIcon size={14} color={colors.grey[500]} />
            <CustomText text={backLabel} useTranslationText={false} size="xs" weight="normal" style={styles.backText} />
          </Pressable>
        </View>
        <CustomText text={title} useTranslationText={false} size="m" weight="bold" style={styles.title} />
      </View>

      <View style={styles.body}>{children}</View>

      {footer ? <View style={styles.footer}>{footer}</View> : null}

      {showCloseButton ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close gap action"
          onPress={onClose}
          style={styles.closeButton}>
          <XIcon size={16} color={colors.grey[700]} />
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  drawerShell: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    gap: 12,
    flexShrink: 0,
  },
  headerTopRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderRadius: 4,
    paddingVertical: 2,
    paddingRight: 4,
  },
  backText: {
    color: colors.grey[500],
  },
  title: {
    color: colors.grey[900],
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  footer: {
    minHeight: 48,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: 20,
    justifyContent: "center",
    flexShrink: 0,
  },
  closeButton: {
    position: "absolute",
    right: 16,
    top: 16,
    width: 24,
    height: 24,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.7,
  },
});

export default GapActionDrawerLayout;
